import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isGoogleOAuthAvailable } from '../config/amplify-config';

interface GoogleSignInButtonProps {
  disabled?: boolean;
  className?: string;
  text?: string;
}

/**
 * Google Sign-In Button Component using Amplify Federated Sign-In
 * 
 * This button uses AWS Amplify's built-in federated authentication
 * which integrates Google OAuth with AWS Cognito.
 * 
 * Configuration is automatically loaded from:
 * 1. Infrastructure outputs (CognitoOAuthDomainUrl) - preferred
 * 2. Environment variables (REACT_APP_COGNITO_OAUTH_DOMAIN) - fallback
 * 
 * The button only renders when OAuth configuration is available.
 */
export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  disabled = false,
  className = '',
  text = 'Continue with Google'
}) => {
  const { signInWithProvider, isLoading } = useAuth();

  // Check if OAuth is configured via infrastructure outputs or environment variables
  const isOAuthConfigured = isGoogleOAuthAvailable();

  // Don't render if OAuth is not configured
  if (!isOAuthConfigured) {
    return null;
  }

  const handleGoogleSignIn = async () => {
    if (disabled || isLoading) return;

    try {
      // This will redirect to Google OAuth and then back to Cognito
      await signInWithProvider('Google');
    } catch (error) {
      // Error handling is done in AuthContext
      console.error('Google sign-in error:', error);
    }
  };

  // Button styles using Tailwind
  const baseStyles = `
    flex items-center justify-center gap-3 px-6 py-3 rounded-lg font-medium
    transition-all duration-200 ease-in-out
    disabled:opacity-50 disabled:cursor-not-allowed
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
    bg-white border border-gray-300 text-gray-700
    hover:bg-gray-50 hover:border-gray-400
    shadow-sm
  `;

  const GoogleIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      className="flex-shrink-0"
    >
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );

  return (
    <button
      onClick={handleGoogleSignIn}
      disabled={disabled || isLoading}
      className={`${baseStyles} ${className}`}
      type="button"
    >
      {isLoading ? (
        <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
      ) : (
        <GoogleIcon />
      )}
      <span className="text-sm">
        {isLoading ? 'Signing in...' : text}
      </span>
    </button>
  );
};

export default GoogleSignInButton;